import type { Editor } from '@tiptap/react';
import { useCallback, useEffect, useRef } from 'react';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { useUpdateExperience } from '@/features/experiences/hooks/useUpdateExperience';
import { printVisibility } from '@/features/styles/lib/printStyles';
import { useDebounceCallback } from '@/hooks/useDebounceCallback';
import { syncEditorContent } from '@/pages/Schedule/components/RichTextEditor';
import RichTextEditor from '@/pages/Schedule/components/RichTextEditor/RichTextEditor';
import { Box } from '@/ui';

const MAX_CHARACTERS = 500; // About 5 lines of text

export const DescriptionWrite = () => {
  const { experience } = usePageExperienceContext();
  const { setDescription } = useUpdateExperience();
  const editorRef = useRef<Editor>(null);

  const save = useCallback(() => {
    const editor = editorRef.current;
    // A destroyed editor is truthy but its `getHTML()` throws, so guard the
    // debounced/blur save the same way as the content sync (WEB-CLIENT-277).
    if (!editor || editor.isDestroyed) return;
    setDescription(editor.getHTML());
  }, [setDescription]);

  const handleBlur = () => {
    save();
    cancel();
  };

  const { callback: handleChange, cancel } = useDebounceCallback(save, 1000);

  // TipTap's `useEditor` reads `content` only at construction time, so
  // external description changes (e.g. from chat) don't reach the editor on
  // their own. `emitUpdate: false` keeps the sync from round-tripping a save
  // back to the server.
  const incoming = experience?.description ?? '';
  useEffect(() => {
    syncEditorContent(editorRef.current, incoming, { emitUpdate: false });
  }, [incoming]);

  return (
    <Box sx={printVisibility.hide()} w="100%">
      <RichTextEditor
        containerProps={{ width: '100%' }}
        maxLength={MAX_CHARACTERS}
        placeholder="Add a description"
        ref={editorRef}
        style={{ width: '100%' }}
        value={experience?.description ?? ''}
        onBlur={handleBlur}
        onChange={handleChange}
      />
    </Box>
  );
};
